"use client"

import { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Loader2 } from "lucide-react"
import {
  SUSPENSION_OPERATIONAL_REASONS,
  confirmContinueWithoutImpedimento,
} from "../../constants/suspensionOperationalReasons"
import type { SuspensionLineDto } from "../../services/contracts/ContractLineService"
import { formatPhone } from "../../lib/format-utils"

export interface SuspensionTracePayload {
  motivoOperacional?: string
  clearMotivo?: boolean
  dataPrevistaSuspensao?: string
  clearDataPrevista?: boolean
}

interface SuspensionTraceDialogProps {
  open: boolean
  lines: SuspensionLineDto[]
  onClose: () => void
  onSave: (
    lineIds: string[],
    payload: SuspensionTracePayload
  ) => Promise<{ ok: boolean }>
}

export function SuspensionTraceDialog({ open, lines, onClose, onSave }: SuspensionTraceDialogProps) {
  const [impedimento, setImpedimento] = useState("")
  const [dataPrevista, setDataPrevista] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const isBulk = lines.length > 1
  const singleLine = lines.length === 1 ? lines[0] : null

  useEffect(() => {
    if (!open || lines.length === 0) return
    if (singleLine) {
      setImpedimento(singleLine.motivoOperacional ?? "")
      setDataPrevista(
        singleLine.dataPrevistaSuspensao ? singleLine.dataPrevistaSuspensao.slice(0, 10) : ""
      )
    } else {
      setImpedimento("")
      setDataPrevista("")
    }
    setSubmitting(false)
  }, [open, lines, singleLine])

  const handleSubmit = async () => {
    if (lines.length === 0) return
    if (!confirmContinueWithoutImpedimento(impedimento || undefined)) return

    const payload: SuspensionTracePayload = {}
    if (!impedimento) {
      payload.clearMotivo = true
    } else {
      payload.motivoOperacional = impedimento
    }
    if (!dataPrevista) {
      payload.clearDataPrevista = true
    } else {
      payload.dataPrevistaSuspensao = dataPrevista
    }

    setSubmitting(true)
    try {
      const result = await onSave(
        lines.map((l) => l.id),
        payload
      )
      if (result.ok) onClose()
    } catch {
      // Rejeição inesperada: o diálogo fica aberto e o erro não vai ao Sentry como crash.
    } finally {
      setSubmitting(false)
    }
  }

  if (!open || lines.length === 0) return null

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Registar impedimento</DialogTitle>
          <DialogDescription>
            {isBulk ? (
              <>
                Aplicar o mesmo impedimento e data prevista a{" "}
                <strong>{lines.length} linhas pendentes</strong> selecionadas.
              </>
            ) : (
              <>
                Linha {formatPhone(singleLine!.ddd, singleLine!.numero)} — quando não foi possível
                suspender na TIM.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isBulk && (
          <ul className="text-xs text-muted-foreground max-h-24 overflow-y-auto border rounded-md p-2 space-y-1">
            {lines.map((l) => (
              <li key={l.id}>
                {formatPhone(l.ddd, l.numero)}
                {l.planName ? ` · ${l.planName}` : ""}
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>Impedimento</Label>
            <Select
              value={impedimento || "__none__"}
              onValueChange={(v) => setImpedimento(v === "__none__" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o impedimento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Sem impedimento —</SelectItem>
                {SUSPENSION_OPERATIONAL_REASONS.map((r) => (
                  <SelectItem key={r.code} value={r.code}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="traceDataPrevista">Data em que deveria suspender (opcional)</Label>
            <Input
              id="traceDataPrevista"
              type="date"
              value={dataPrevista}
              onChange={(e) => setDataPrevista(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isBulk ? (
              `Guardar em ${lines.length} linhas`
            ) : (
              "Guardar"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
